import React, { useState,useEffect, useMemo } from 'react';
import Iconify from 'src/components/Iconify';
import {
  InputAdornment,
  Grid,
  Box,
  Autocomplete,
  Checkbox,
  Button,
  TextField,
  Typography,
  TableCell,
  TableRow,
  TableBody,
  Table,
  TableHead,
  TableContainer,
  TablePagination,
  OutlinedInput,
  Link as MuiLink,
  createFilterOptions,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';
import Scrollbar from 'src/components/Scrollbar';
import { monEscalationReport } from 'src/api/reports';
import SearchNotFound from 'src/components/SearchNotFound';
import * as XLSX from 'xlsx';
import CircularProgress from '@mui/material/CircularProgress';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { getClientProperties } from 'src/api/ShiftMaster';


  const months = [
    { name: 'January', value: '1' },
    { name: 'February', value: '2' },
    { name: 'March', value: '3' },
    { name: 'April', value: '4' },
    { name: 'May', value: '5' },
    { name: 'June', value: '6' },
    { name: 'July', value: '7' },
    { name: 'August', value: '8' },
    { name: 'September', value: '9' },
    { name: 'October', value: '10' },
    { name: 'November', value: '11' },
    { name: 'December', value: '12' }
  ];


const MonthlyEscalationReport = ({clientList}) => {
  const theme = useTheme();

  const iconLocation = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIconLocation = <CheckBoxIcon fontSize="small" />;

  const [filterName, setFilterName] = useState('');

  const [value1, setValue1] = useState(null);

  const [value3, setValue3] = useState(null);

  const [value2, setValue2] = useState(null);
  const currentYear = new Date().getFullYear();
  const years = [(currentYear - 1).toString(),currentYear.toString(),(currentYear+1).toString()];

  const [error,setError]=useState(false)
  const [isMonthSelected,setIsMonthSelected]=useState(false)
  const [isYearSelected,setIsYearSelected]=useState(false)
  const [isClientSelected,setIsClientSelected]=useState(false)



const [data, setData] = useState([])
const [row,rowChange] = useState([])
const [page,pageChange]=useState(0)
const [rowPerPage,rowPerPageChange]=useState(100)

const [headers, setHeaders] = useState([]);
const [dataReport, setDataReport] = useState([]);


const [loading,setLoading] = useState(false)
const [locations, setLocations] = useState([]);
const [selectedLocations, setSelectedLocations] = useState([]);
const [openLocationAuto, setOpenLocationAuto] = useState(false);
const [isLocationSelected, setIsLocationSelected] = useState(false);

const normalizeDisplayName = (name) =>
  String(name || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const buildUniqueClientOptions = (clients) => {
  const grouped = new Map();

  (clients || []).forEach((client) => {
    const key = normalizeDisplayName(client?.displayname);
    if (!key) return;

    if (!grouped.has(key)) {
      grouped.set(key, []);
    }

    grouped.get(key).push(client);
  });

  return Array.from(grouped.values()).map((groupItems) => {
    if (groupItems.length === 1) return groupItems[0];

    const assignTargets = new Set(
      groupItems
        .map((item) => Number(item.AssignClient || 0))
        .filter((assignId) => assignId > 0)
    );

    const mappedMain = groupItems.find((item) => assignTargets.has(Number(item.ID)));
    if (mappedMain) return mappedMain;

    const rootEntry = groupItems.find((item) => Number(item.AssignClient || 0) === 0);
    return rootEntry || groupItems[0];
  });
};

const uniqueClientOptions = useMemo(
  () => buildUniqueClientOptions(clientList || []),
  [clientList]
);
const selectedLocationOptions = useMemo(
  () => locations.filter((loc) => selectedLocations.includes(loc.ID || loc.id)),
  [locations, selectedLocations]
);

useEffect(() => {
  if (!value3?.ID) {
    setLocations([]);
    setSelectedLocations([]);
    setOpenLocationAuto(false);
    return;
  }

  getClientProperties({ AssignClient: String(value3.ID) }, (response) => {
    if (response.status === 200) {
      const uniqueLocations = Array.from(
        new Map(
          (response.data || [])
            .map((loc) => ({
              ...loc,
              ID: Number(loc?.ID ?? loc?.id ?? 0),
              Location: String(loc?.Location || loc?.name || '').trim(),
            }))
            .filter((loc) => loc.ID > 0)
            .map((loc) => [loc.ID, loc])
        ).values()
      );
      setLocations(uniqueLocations);
      setSelectedLocations([]);
      setOpenLocationAuto(false);
    } else {
      setLocations([]);
      setSelectedLocations([]);
      setOpenLocationAuto(false);
    }
  });
}, [value3]);

const handleLocationChange = (event, value) => {
  if (value.some((option) => option.selectall)) {
    if (selectedLocations.length === locations.length) {
      setSelectedLocations([]);
    } else {
      setSelectedLocations(locations.map((item) => item.ID || item.id));
    }
  } else {
    setSelectedLocations(value.map((item) => item.ID || item.id));
  }
  setIsLocationSelected(false);
};

const filterLocationOptions = (options, params) => {
  const filter = createFilterOptions();
  const filtered = filter(options, params);
  return options.length > 0 ? [{ title: 'Select All', selectall: true }, ...filtered] : filtered;
};

useEffect(() => {
    if (dataReport.length > 0 && headers.length > 0) {
      const ws = XLSX.utils.aoa_to_sheet(dataReport);
      const sheetName = "Monthly Escalation Report";
      const filename = sheetName + '.xlsx';

      const maxColumnWidths = {};
      headers.forEach(header => {
        maxColumnWidths[header] = Math.max(
          20,
          ...data.map(row => (row[header] || '').toString().length)
        );
      });
      const columnWidths = headers.map(header => ({
        wch: maxColumnWidths[header]
      }));

      ws['!cols'] = columnWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sheetName || 'Sheet 1');

      XLSX.writeFile(wb, filename);
    }
  }, [dataReport, headers]);


const handleButtonClick = () => {
    pageChange(0)

    setData([])
    if(!value1){
        setError(true)
        setIsMonthSelected(true)
    }

    if(!value2){
        setError(true)
        setIsYearSelected(true)
    }

    if(!value3){
        setError(true)
        setIsClientSelected(true)
    }
    if(locations.length > 0 && selectedLocations.length===0){
        setError(true)
        setIsLocationSelected(true)
    }
    if(value1 && value2 && value3 && (locations.length === 0 || selectedLocations.length>0)){
        setLoading(true)
        let month=value1.value
        let year=value2
        let clientId = selectedLocations.length > 0
          ? selectedLocations.join(',')
          : value3.ID
        let data={month:month,year:year,client_id:clientId}
        monEscalationReport(data,(result)=>{
          setLoading(false)
           if(result.status===200){
             setData(result.data)
           }
        })
    } else {
      setLoading(false)
    }
  };

    const columns =
    [
        { id: 'Sno', name: 'Sno' },
        { id: 'EventNo', name: 'Event Number' },
        { id: 'devicename', name: 'Camera Name' },
        { id: 'deviceid', name: 'Camera Id' },
        { id: 'Location', name: 'Location' },
        { id: 'VideoUrl', name: 'Escalation Url' }
    ]

const handleChangePage = (event,newpage)=>{
    if(newpage===0){
        pageChange(0);
    }
    else{
        pageChange(newpage)
    }
}

const handleRowsPerPage = (e)=>{
    rowPerPageChange(e.target.value)
    pageChange(0)
}

const handleFilterByName = (event) => {
    const pattern=event.target.value.trim()
    setFilterName(pattern);
  };

  const formatLocations = (locationValue) => {
    if (Array.isArray(locationValue)) {
      return locationValue
        .map((val) => String(val || '').trim())
        .filter(Boolean)
        .join(', ');
    }

    if (typeof locationValue === 'string') {
      return locationValue
        .split(',')
        .map((val) => val.trim())
        .filter(Boolean)
        .join(', ');
    }

    return '';
  };

  const filteredData = data.filter((item) => {
    const locationText = formatLocations(item.Location);
    return (
      (item.EventNo && item.EventNo.toLowerCase().includes(filterName.toLowerCase())) ||
      (item.devicename && item.devicename.toLowerCase().includes(filterName.toLowerCase())) ||
      (item.VideoUrl && item.VideoUrl.toLowerCase().includes(filterName.toLowerCase())) ||
      (locationText && locationText.toLowerCase().includes(filterName.toLowerCase()))
    );
  });

  const isDataNotFound = filteredData.length === 0;

  MonthlyEscalationReport.prototype = {
    numSelected: PropTypes.number,
    filterName: PropTypes.string,
    onFilterName: PropTypes.func,
  }

  const exportToExcel = () => {
        if (data.length > 0) {
            const newColumnOrder = [
                'S No',
                'Event No',
                'Camera Name',
                'Camera Id',
                'Location',
                'Escalation Url'
            ];

            const headers = newColumnOrder;

            const dataReport = [headers, ...data.map((item, index) =>
                [index + 1, ...headers.slice(1).map(key => {
                    if (key === 'Event No') return item['EventNo'];
                    if (key === 'Camera Name') return item['devicename'];
                    if (key === 'Camera Id') return item['CameraId'];
                    if (key === 'Location') return formatLocations(item['Location']);
                    if (key === 'Escalation Url') return item['VideoUrl'];
                    return item[key];
                })]
            )];

            setHeaders(headers);
            setDataReport(dataReport);
            }
        else {
            setHeaders([]);
            setDataReport([]);
        alert('No data to Export.');
        return;
        }
  };

  return (
    <Box>
      {/* Filter + Search */}
      <Box sx={{ px: 2, pt: 2 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={2}>
            <Autocomplete
              value={value1}
              onChange={(event, newValue) => { setIsMonthSelected(false); setValue1(newValue); }}
              options={months}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Month" error={error && isMonthSelected} />
              )}
            />
            {error && isMonthSelected && <Typography variant="caption" color="error">Select Month</Typography>}
          </Grid>
          <Grid item xs={12} sm={2}>
            <Autocomplete
              value={value2}
              onChange={(event, newValue) => { setIsYearSelected(false); setValue2(newValue); }}
              options={years}
              getOptionLabel={(option) => option}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Year" error={error && isYearSelected} />
              )}
            />
            {error && isYearSelected && <Typography variant="caption" color="error">Select Year</Typography>}
          </Grid>
          <Grid item xs={12} sm={3}>
            <Autocomplete
              value={value3}
              onChange={(event, newValue) => { setIsClientSelected(false); setValue3(newValue); }}
              options={uniqueClientOptions}
              getOptionLabel={(option) => `${option.displayname || ''}`}
              isOptionEqualToValue={(option, value) => option.ID === value.ID}
              renderOption={(props, option) => (
                <li {...props} key={option.ID}>{option.displayname}</li>
              )}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Client" error={error && isClientSelected} />
              )}
            />
            {error && isClientSelected && <Typography variant="caption" color="error">Select Client</Typography>}
          </Grid>
          {value3 && locations.length > 0 && (
            <Grid item xs={12} sm={3}>
              <Autocomplete
                multiple
                fullWidth
                options={locations}
                value={selectedLocationOptions}
                getOptionLabel={(option) =>
                  option.selectall ? option.title : `${option.Location || option.name || ''}`
                }
                onChange={handleLocationChange}
                disableCloseOnSelect={openLocationAuto}
                open={openLocationAuto}
                onOpen={() => setOpenLocationAuto(true)}
                onClose={() => setOpenLocationAuto(false)}
                filterOptions={filterLocationOptions}
                renderTags={() => null}
                renderOption={(props, option) => {
                  const key = `locid-${option.ID || option.id || option.title}`;
                  return (
                    <li {...props} key={key}>
                      {option.selectall ? (
                        <>
                          <Checkbox
                            icon={iconLocation}
                            checkedIcon={checkedIconLocation}
                            style={{ marginRight: 8 }}
                            checked={locations.length > 0 && selectedLocations.length === locations.length}
                          />
                          {option.title}
                        </>
                      ) : (
                        <>
                          <Checkbox
                            icon={iconLocation}
                            checkedIcon={checkedIconLocation}
                            style={{ marginRight: 8 }}
                            checked={selectedLocations.includes(option.ID || option.id)}
                          />
                          {`${option.Location || option.name || ''}`}
                        </>
                      )}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Locations"
                    error={error && isLocationSelected}
                  />
                )}
              />
              {error && isLocationSelected && <Typography variant="caption" color="error">Select Locations</Typography>}
            </Grid>
          )}
          <Grid item xs={12} sm={2} sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" onClick={handleButtonClick}>Submit</Button>
            <Button variant="outlined" onClick={exportToExcel}>Export</Button>
          </Grid>
        </Grid>

        <Box sx={{ mb: 2 }}>
          <OutlinedInput
            value={filterName}
            onChange={handleFilterByName}
            placeholder="Search"
            startAdornment={
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
              </InputAdornment>
            }
            sx={{ width: 220 }}
          />
        </Box>
      </Box>

      {/* Table — full width, no outer padding */}
      <Scrollbar>
        <TableContainer
          sx={{
            maxHeight: 520,
            minWidth: 800,
            "&::-webkit-scrollbar": { width: 6, height: 6 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: alpha(theme.palette.text.primary, 0.2),
              borderRadius: 3,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: alpha(theme.palette.text.primary, 0.35),
            },
          }}
        >
          <Table stickyHeader>
            <TableHead sx={{ '& th': { bgcolor: 'background.neutral', fontWeight: 600 } }}>
              <TableRow>
                {columns.map(item => (
                  <TableCell key={item.id}>{item.name}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : filteredData && filteredData.length > 0 ? (
                filteredData
                  .slice(page * rowPerPage, (page * rowPerPage) + rowPerPage)
                  .map((row, index) => (
                    <TableRow key={row.id} hover>
                      <TableCell align="left">{index + 1}</TableCell>
                      <TableCell align="left">{row.EventNo}</TableCell>
                      <TableCell align="left">{row.devicename}</TableCell>
                      <TableCell align="left">{row.CameraId}</TableCell>
                      <TableCell align="left">
                        {formatLocations(row.Location)
                          ? formatLocations(row.Location).split(',').map((loc, locationIndex) => (
                              <Box
                                key={`${row.id || row.EventNo || index}-${loc}-${locationIndex}`}
                                sx={{
                                  borderBottom:
                                    locationIndex !== formatLocations(row.Location).split(',').length - 1
                                      ? '1px solid #ddd'
                                      : 'none',
                                  py: 0.5,
                                }}
                              >
                                {loc}
                              </Box>
                            ))
                          : ''}
                      </TableCell>
                      <TableCell align="left">
                        <MuiLink
                          href={row.VideoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          color="primary"
                          underline="hover"
                        >
                          {row.VideoUrl}
                        </MuiLink>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (filteredData.length === 0 && filterName) ? (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <SearchNotFound searchQuery={filterName} />
                  </TableCell>
                </TableRow>
              ) : (filteredData.length === 0 && !filterName) && (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <Typography variant="body1" align="center" sx={{ fontWeight: 'bold' }}>
                      No Data
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>

      <TablePagination
        rowsPerPageOptions={[100, 300, 500]}
        page={page}
        count={filteredData.length}
        rowsPerPage={rowPerPage}
        component="div"
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleRowsPerPage}
        sx={{ borderTop: 1, borderColor: 'divider' }}
      />
    </Box>
  );
};

export default MonthlyEscalationReport;
